import type { Awareness } from 'y-protocols/awareness.js';

export interface UserInfo {
  id?: string;
  name: string;
  avatar?: string | null;
}

type UserSelection = Array<Record<string, unknown>>;

/**
 * Viewport state for collaborative presentation sync.
 * Broadcast by presenter, consumed by followers.
 */
export interface ViewportState {
  /** Current zoom level, constrained by the active canvas viewport. */
  zoom: number;
  /** Viewport center X coordinate */
  centerX: number;
  /** Viewport center Y coordinate */
  centerY: number;
  /** Visible world rectangle. Used by followers to fit the same presenter view across viewport sizes. */
  visibleBounds?: {
    x: number;
    y: number;
    w: number;
    h: number;
  };
  /** Current frame index in presentation mode (0-based) */
  frameIndex?: number;
  /** Current frame ID for validation */
  frameId?: string;
}

/**
 * Presentation display settings.
 * Synced from presenter to followers.
 */
export interface PresentationSettings {
  /** Frame fills entire screen vs fits within bounds */
  fillScreen: boolean;
  /** Black vignette background around frame */
  blackBackground: boolean;
  /** Hide the presentation toolbar */
  hideToolbar: boolean;
}

/**
 * Presentation mode type.
 * - 'free': Continuous viewport sync (pan/zoom)
 * - 'frames': Frame-based navigation sync
 */
export type PresentationMode = 'free' | 'frames';

/**
 * Collaborative presentation state.
 * Only one user per document can be presenting at a time.
 */
export interface PresentationState {
  /** Whether this user is currently presenting */
  isPresenting: boolean;
  /** Current presentation mode - free viewport or frame-based */
  mode?: PresentationMode;
  /** Whether to show follower cursors (presenter-only control) */
  showFollowerCursors?: boolean;
  /** Presentation display settings */
  settings?: PresentationSettings;
}

export interface DatabaseRowFocusState {
  /** Rendering context: canvas/fullscreen/page. Keeps visual focus local to a UI surface. */
  scope: string;
  /** Database block id */
  databaseId: string;
  /** Data-view id within the database */
  viewId: string;
  /** Row/card/task id */
  rowId: string;
  /** Client timestamp for stale/future conflict handling */
  updatedAt: number;
}

export interface DatabaseCellPresenceState {
  /** Caret selection length inside a text-like cell, when editing. */
  caretLength?: number;
  /** Caret offset inside a text-like cell, when editing. */
  caretOffset?: number;
  /** Rendering context: parent page doc + database id + cell. Shared across UI surfaces. */
  scope: string;
  /** Database block id */
  databaseId: string;
  /** Data-view id within the database */
  viewId: string;
  /** Row id */
  rowId: string;
  /** Column/property id */
  columnId: string;
  /** Whether the remote user is selecting or editing the cell */
  mode: 'selected' | 'editing';
  /** Client timestamp for stale/future conflict handling */
  updatedAt: number;
}

export interface DatabaseAreaSelectionPresenceState {
  /** Rendering context: parent page doc + database id + table area. */
  scope: string;
  /** Database block id */
  databaseId: string;
  /** Data-view id within the database */
  viewId: string;
  /** Selected row ids in current table view order. */
  rowIds: string[];
  /** Selected column/property ids in current table view order. */
  columnIds: string[];
  /** Client timestamp for stale/future conflict handling */
  updatedAt: number;
}

export type DatabaseCanvasUiWindowState = {
  propertyLimit?: number;
  propertyOffset?: number;
  rowLimit?: number;
  rowOffset?: number;
};

export type DatabaseCanvasUiTableViewState = {
  columnOrder?: readonly string[];
  columnWidths?: Readonly<Record<string, number>>;
};

export type DatabaseCanvasUiSearchState = {
  expanded: boolean;
  query: string;
};

export type DatabaseCanvasUiPresentationState =
  | {
      mode: 'summary';
      updatedAt?: number;
    }
  | {
      mode: 'live';
      viewId: string;
      viewport: {
        rowAnchor?: {
          offsetPx: number;
          offsetRatio: number;
          rowId: string;
        };
        scrollLeft: number;
        scrollTop: number;
        target?: string;
        targets?: Record<
          string,
          {
            rowAnchor?: {
              offsetPx: number;
              offsetRatio: number;
              rowId: string;
            };
            scrollLeft: number;
            scrollTop: number;
          }
        >;
      };
      updatedAt?: number;
    };

export interface DatabaseCanvasUiState {
  /** Rendering context: parent page doc + database id + canvas surface. */
  scope: string;
  /** Database block id */
  databaseId: string;
  /** Shared canvas presentation mode/view/scroll state. */
  presentation: DatabaseCanvasUiPresentationState;
  /** Shared large-data pagination/window state per view. */
  windows?: Record<string, DatabaseCanvasUiWindowState>;
  /** Shared table presentation state per view. */
  tableViews?: Record<string, DatabaseCanvasUiTableViewState>;
  /** Shared search expansion, query, and resulting row filter per view. */
  searches?: Record<string, DatabaseCanvasUiSearchState>;
  /** Client timestamp for last-writer-wins live UI state. */
  updatedAt: number;
}

export interface DatabaseFullscreenPresenceState {
  /** Rendering context: parent page doc + fullscreen surface. */
  scope: string;
  /** Database block id currently opened in fullscreen. */
  databaseId: string;
  /** Client timestamp for stale cleanup/debugging. */
  updatedAt: number;
}

export interface AttachmentMediaPresenceState {
  /** Rendering context: parent page doc. */
  scope: string;
  /** Attachment block id. */
  attachmentId: string;
  /** Blob/source id when available, used only as extra identity. */
  sourceId?: string;
  /** Human readable file name. */
  name: string;
  /** Local attachment media kind. */
  mediaKind: 'audio' | 'video';
  /** Last user-visible media action. */
  action: 'play' | 'pause' | 'seek' | 'rate' | 'volume' | 'time';
  /** Current media time in seconds at updatedAt. */
  currentTime: number;
  /** Known media duration in seconds. */
  duration?: number;
  /** Whether the publisher is currently playing. */
  playing: boolean;
  /** Whether the publisher's local media is muted. */
  muted: boolean;
  /** Playback rate at updatedAt. */
  playbackRate: number;
  /** Whether this event was published by a presenter/moderator. */
  moderated?: boolean;
  /** Client timestamp for last-writer-wins live UI state. */
  updatedAt: number;
}

export interface CanvasDragPreviewElementState {
  /** Canvas block or surface element id. */
  elementId: string;
  /** Element bound for this preview frame, in model coordinates. */
  xywh: string;
  /** Element rotation for this preview frame, in degrees. */
  rotate?: number;
}

export interface CanvasDragPreviewState {
  /** Unique per-drag id. */
  dragId: string;
  /** Dragged elements with their preview bounds. */
  elements: CanvasDragPreviewElementState[];
  /** Fast lookup for interaction locks. */
  elementIds: string[];
  /** Preview operation. All operations publish actual frame bounds. */
  operation: 'move' | 'resize' | 'rotate';
  /** Model-space cursor position at drag start. */
  startCursor: {
    x: number;
    y: number;
  };
  /** Dragging blocks interaction; settling keeps fixed final bounds visible. */
  status: 'dragging' | 'settling';
  /** Client timestamp for stale cleanup/debugging. */
  updatedAt: number;
}

// Raw JSON state in awareness CRDT
export type RawAwarenessState = {
  user?: UserInfo;
  color?: string;
  selectionV2: Record<string, UserSelection>;
  /** Viewport state for presentation sync */
  viewport?: ViewportState;
  /** Collaborative presentation state */
  presentation?: PresentationState;
  /** Ephemeral database row/card/task focus for collaborative attention. */
  databaseRowFocus?: DatabaseRowFocusState;
  /** Ephemeral database cell presence shared between canvas and fullscreen. */
  databaseCellPresence?: DatabaseCellPresenceState;
  /** Ephemeral database multi-cell area selection presence. */
  databaseAreaSelectionPresence?: DatabaseAreaSelectionPresenceState;
  /** Ephemeral edgeless database canvas UI state shared through page awareness. */
  databaseCanvasUi?: DatabaseCanvasUiState;
  /** Ephemeral database fullscreen presence, shown as a non-selection badge. */
  databaseFullscreenPresence?: DatabaseFullscreenPresenceState;
  /** Ephemeral media control presence for attachment audio/video playback. */
  attachmentMediaPresence?: AttachmentMediaPresenceState;
  /** Ephemeral canvas drag preview, rendered from the same presence cursor. */
  canvasDragPreview?: CanvasDragPreviewState;
};

export interface AwarenessEvent {
  id: number;
  type: 'add' | 'update' | 'remove';
  state?: RawAwarenessState;
}

export class AwarenessStore {
  readonly awareness: Awareness;

  constructor(awareness: Awareness) {
    this.awareness = awareness;
    this.awareness.setLocalStateField('selectionV2', {});
  }

  destroy() {
    this.awareness.destroy();
  }

  getLocalSelection(
    selectionManagerId: string
  ): ReadonlyArray<Record<string, unknown>> {
    return (
      (this.awareness.getLocalState()?.selectionV2 ?? {})[selectionManagerId] ??
      []
    );
  }

  getStates(): Map<number, RawAwarenessState> {
    return this.awareness.getStates() as Map<number, RawAwarenessState>;
  }

  getLocalState(): RawAwarenessState {
    return this.awareness.getLocalState() as RawAwarenessState;
  }

  setLocalState(state: RawAwarenessState): void {
    this.awareness.setLocalState(state);
  }

  setLocalStateField<Field extends keyof RawAwarenessState>(
    field: Field,
    value: RawAwarenessState[Field]
  ): void {
    this.awareness.setLocalStateField(field, value);
  }

  setLocalSelection(selectionManagerId: string, selection: UserSelection) {
    const oldSelection = this.awareness.getLocalState()?.selectionV2 ?? {};
    this.awareness.setLocalStateField('selectionV2', {
      ...oldSelection,
      [selectionManagerId]: selection,
    });
  }
}
